import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import { notificationTopicDisplayLabel } from "@/lib/notificationsInboxDisplay";
import {
  readNotificationArrivalDismissMs,
  rowsForSurfaceEventIds,
} from "@/lib/notificationArrival";
import { notificationsUnreadCountLiveQueryOptions } from "@/lib/notificationsUnreadCountLive";
import {
  fetchNotificationsList,
  markNotificationRead,
  notificationsUnreadCountQueryKey,
  snoozeNotification,
  type NotificationListRow,
  type NotificationUnreadCount,
} from "@/lib/notificationsApi";
import {
  datetimeLocalValueToIso,
  defaultCustomSnoozeLocalValue,
  isNotificationActivelySnoozed,
  snoozePresetUntilIso,
} from "@/lib/notificationsSnooze";
import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";

type ArrivalCard = {
  row: NotificationListRow;
  addedAt: number;
  dismissMs: number;
};

type AnchorBox = {
  top: number;
  right: number;
};

function snoozeWakeDelayMs(snoozedUntil: string, nowMs: number): number | null {
  const untilMs = new Date(snoozedUntil).getTime();
  if (Number.isNaN(untilMs) || untilMs <= nowMs) {
    return null;
  }
  return Math.min(untilMs - nowMs, 2_147_000_000);
}

export function NotificationArrivalWindow({
  anchorRef,
}: {
  anchorRef: RefObject<HTMLElement | null>;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [cards, setCards] = useState<ArrivalCard[]>([]);
  const [box, setBox] = useState<AnchorBox | null>(null);
  const hiddenEventIds = useRef(new Set<string>());
  const seenArrivalTokens = useRef(new Set<number>());
  const dismissMsRef = useRef(readNotificationArrivalDismissMs());

  const unread = useQuery({
    ...notificationsUnreadCountLiveQueryOptions,
    enabled: false,
  }).data;

  const snoozedQuery = useQuery({
    queryKey: ["notifications", "arrival-snoozes"],
    queryFn: () => fetchNotificationsList({ filter: "snoozed", limit: 50 }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (anchor == null) {
      setBox(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setBox({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    place();
    return registerViewportLayoutListeners(place);
  }, [place, cards.length]);

  const pushRows = useCallback((rows: NotificationListRow[]) => {
    if (rows.length === 0) {
      return;
    }
    const dismissMs = readNotificationArrivalDismissMs();
    dismissMsRef.current = dismissMs;
    setCards((current) => {
      const seen = new Set(current.map((card) => card.row.eventId));
      const next = [...current];
      for (const row of rows) {
        if (seen.has(row.eventId) || hiddenEventIds.current.has(row.eventId)) {
          continue;
        }
        if (isNotificationActivelySnoozed(row.snoozedUntil)) {
          continue;
        }
        seen.add(row.eventId);
        next.push({ row, addedAt: Date.now(), dismissMs });
      }
      return next;
    });
  }, []);

  const dismissCard = useCallback((eventId: string) => {
    hiddenEventIds.current.add(eventId);
    setCards((current) => current.filter((card) => card.row.eventId !== eventId));
  }, []);

  const releaseForReturn = useCallback((eventId: string) => {
    hiddenEventIds.current.delete(eventId);
    setCards((current) => current.filter((card) => card.row.eventId !== eventId));
  }, []);

  useEffect(() => {
    const token = unread?.arrivalToken;
    const surfaceEventIds = unread?.surfaceEventIds ?? [];
    if (token == null || surfaceEventIds.length === 0 || seenArrivalTokens.current.has(token)) {
      return;
    }
    seenArrivalTokens.current.add(token);
    let cancelled = false;
    void fetchNotificationsList({ filter: "unread", limit: 20 }).then((result) => {
      if (cancelled) {
        return;
      }
      pushRows(rowsForSurfaceEventIds(result.rows, surfaceEventIds, hiddenEventIds.current));
    }).catch(() => {
      /* The badge still updated from the live stream. */
    });
    return () => {
      cancelled = true;
    };
  }, [pushRows, unread?.arrivalToken, unread?.surfaceEventIds]);

  useEffect(() => {
    const rows = snoozedQuery.data?.rows ?? [];
    const timers: number[] = [];
    const nowMs = Date.now();
    for (const row of rows) {
      if (row.snoozedUntil == null) {
        continue;
      }
      const delay = snoozeWakeDelayMs(row.snoozedUntil, nowMs);
      if (delay == null) {
        continue;
      }
      timers.push(window.setTimeout(() => {
        releaseForReturn(row.eventId);
        pushRows([{ ...row, snoozedUntil: null, readAt: null }]);
        queryClient.setQueryData<{ rows: NotificationListRow[] }>(
          ["notifications", "arrival-snoozes"],
          (current) => current == null
            ? current
            : { ...current, rows: current.rows.filter((item) => item.eventId !== row.eventId) },
        );
        queryClient.setQueryData<NotificationUnreadCount>(notificationsUnreadCountQueryKey, (current) => ({
          unreadCount: (current?.unreadCount ?? 0) + 1,
        }));
      }, delay));
    }
    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer);
      }
    };
  }, [pushRows, queryClient, releaseForReturn, snoozedQuery.data]);

  const dismissTimers = useRef(new Map<string, number>());
  useEffect(() => {
    const live = new Set(cards.map((card) => card.row.eventId));
    for (const [eventId, timer] of dismissTimers.current) {
      if (!live.has(eventId)) {
        window.clearTimeout(timer);
        dismissTimers.current.delete(eventId);
      }
    }
    for (const card of cards) {
      if (dismissTimers.current.has(card.row.eventId)) {
        continue;
      }
      dismissTimers.current.set(card.row.eventId, window.setTimeout(() => {
        dismissTimers.current.delete(card.row.eventId);
        dismissCard(card.row.eventId);
      }, card.dismissMs));
    }
  }, [cards, dismissCard]);

  if (cards.length === 0 || box == null || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fixed flex max-h-[70vh] w-[22rem] max-w-[calc(100vw-1rem)] flex-col gap-2 overflow-y-auto"
      style={{ top: box.top, right: box.right, zIndex: FLOATING_POPOVER_Z_INDEX }}
      aria-live="polite"
    >
      {cards.map((card) => (
        <ArrivalCardView
          key={`${card.row.eventId}-${card.addedAt}`}
          card={card}
          onHide={() => dismissCard(card.row.eventId)}
          onRead={() => dismissCard(card.row.eventId)}
          onSnoozed={() => releaseForReturn(card.row.eventId)}
          onError={(message) => showToast({ tone: "error", message })}
        />
      ))}
    </div>,
    document.body,
  );
}

function ArrivalCardView({
  card,
  onHide,
  onRead,
  onSnoozed,
  onError,
}: {
  card: ArrivalCard;
  onHide: () => void;
  onRead: () => void;
  onSnoozed: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customUntil, setCustomUntil] = useState(defaultCustomSnoozeLocalValue);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const customFieldId = useId();
  const { row } = card;

  useEffect(() => {
    if (!snoozeOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current != null && !menuRef.current.contains(event.target as Node)) {
        setSnoozeOpen(false);
        setCustomOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [snoozeOpen]);

  const readMutation = useMutation({
    mutationFn: () => markNotificationRead(row.eventId),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationsUnreadCountQueryKey, { unreadCount: data.unreadCount });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
      onRead();
    },
    onError: (error: unknown) => {
      onError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: (until: string) => snoozeNotification(row.eventId, until),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationsUnreadCountQueryKey, { unreadCount: data.unreadCount });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "arrival-snoozes"] });
      onSnoozed();
    },
    onError: (error: unknown) => {
      onError(error instanceof ApiError ? error.message : String(error));
    },
  });

  return (
    <article
      className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
      style={{ animation: `hub-notification-arrival-fade ${card.dismissMs}ms linear forwards` }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {notificationTopicDisplayLabel(row)}
      </p>
      <Link to={row.destinationPath} className="mt-1 block text-sm font-medium text-slate-900 hover:underline">
        {row.title}
      </Link>
      {row.summary ? <p className="mt-1 line-clamp-3 text-sm text-slate-600">{row.summary}</p> : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative" ref={menuRef}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="gap-1"
            disabled={snoozeMutation.isPending || readMutation.isPending}
            isLoading={snoozeMutation.isPending}
            aria-expanded={snoozeOpen}
            aria-haspopup="menu"
            onClick={() => setSnoozeOpen((open) => !open)}
          >
            Snooze
            <ChevronDown className="h-4 w-4" aria-hidden />
          </Button>
          {snoozeOpen ? (
            <div role="menu" className="absolute left-0 top-full z-10 mt-1 min-w-[12rem] rounded-md border border-slate-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                role="menuitem"
                className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => snoozeMutation.mutate(snoozePresetUntilIso("tomorrow"))}
              >
                Tomorrow
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => snoozeMutation.mutate(snoozePresetUntilIso("next_monday"))}
              >
                Next Monday
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => setCustomOpen((open) => !open)}
              >
                Pick date and time
              </button>
              {customOpen ? (
                <div className="space-y-2 border-t border-slate-100 px-3 py-2">
                  <Label htmlFor={customFieldId} className="text-xs">Return after</Label>
                  <input
                    id={customFieldId}
                    type="datetime-local"
                    className="h-8 w-full rounded-md border border-slate-300 px-2 text-sm"
                    value={customUntil}
                    onChange={(event) => setCustomUntil(event.target.value)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      try {
                        snoozeMutation.mutate(datetimeLocalValueToIso(customUntil));
                      } catch (error) {
                        onError(error instanceof Error ? error.message : String(error));
                      }
                    }}
                  >
                    Snooze until then
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={readMutation.isPending || snoozeMutation.isPending}
          isLoading={readMutation.isPending}
          onClick={() => readMutation.mutate()}
        >
          Mark read
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onHide}>
          Hide
        </Button>
      </div>
    </article>
  );
}
